import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useTranslations } from '@/i18n';
import { cn } from '@/lib/utils';
import { useUpdateFuelAmounts } from '@/hooks/trips/useUpdateFuelAmounts';
import type { TripDetailCity, TripCityFuel } from '@/hooks/trips/types';

interface TripFuelManagementCardProps {
  tripId: string;
  cities: TripDetailCity[];
  canEdit: boolean;
}

interface FuelEntry {
  cityId: number;
  cityName: string;
  fuel: TripCityFuel;
}

export function TripFuelManagementCard({ tripId, cities, canEdit }: TripFuelManagementCardProps) {
  const t = useTranslations('tripFuel');
  const formActionsT = useTranslations('tripForm.actions');
  const updateFuelAmounts = useUpdateFuelAmounts();
  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat('ka-GE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    []
  );

  const fuelEntries = useMemo<FuelEntry[]>(() => {
    return cities.flatMap((city) =>
      (city.fuels ?? []).map((fuel) => ({
        cityId: city.id,
        cityName: city.cityName,
        fuel,
      }))
    );
  }, [cities]);

  const originalValues = useMemo<Record<number, number>>(() => {
    return fuelEntries.reduce<Record<number, number>>((accumulator, entry) => {
      accumulator[entry.fuel.id] = entry.fuel.fuelAmount;
      return accumulator;
    }, {});
  }, [fuelEntries]);

  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setValues(
        fuelEntries.reduce<Record<number, string>>((accumulator, entry) => {
          accumulator[entry.fuel.id] = String(entry.fuel.fuelAmount ?? '');
          return accumulator;
        }, {})
      );
    }
  }, [fuelEntries, isEditing]);

  const totalAmount = useMemo(() => {
    return fuelEntries.reduce((sum, entry) => sum + (entry.fuel.fuelAmount ?? 0), 0);
  }, [fuelEntries]);
  const formattedTotalAmount = useMemo(
    () => numberFormatter.format(totalAmount),
    [numberFormatter, totalAmount]
  );

  const handleToggleEdit = () => {
    setError(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setError(null);
    setIsEditing(false);
  };

  const handleAmountChange = (fuelId: number, value: string) => {
    setValues((previous) => ({
      ...previous,
      [fuelId]: value,
    }));
  };

  const hasChanges = useMemo(() => {
    return fuelEntries.some((entry) => {
      const draftValue = values[entry.fuel.id];
      const parsedValue = draftValue != null ? Number(draftValue) : NaN;
      if (Number.isNaN(parsedValue)) {
        return true;
      }

      return parsedValue !== originalValues[entry.fuel.id];
    });
  }, [fuelEntries, originalValues, values]);

  const hasValidationError = useMemo(() => {
    return fuelEntries.some((entry) => {
      const draftValue = values[entry.fuel.id];
      if (draftValue == null || draftValue.trim().length === 0) {
        return true;
      }

      const parsed = Number(draftValue);
      return Number.isNaN(parsed) || parsed < 0;
    });
  }, [fuelEntries, values]);

  const handleSave = async () => {
    setError(null);

    const changes = fuelEntries
      .map((entry) => {
        const draftValue = values[entry.fuel.id];
        if (draftValue == null) {
          return null;
        }

        const parsed = Number(draftValue);
        if (Number.isNaN(parsed) || parsed < 0) {
          return null;
        }

        if (parsed === originalValues[entry.fuel.id]) {
          return null;
        }

        return {
          cityId: entry.cityId,
          fuelId: entry.fuel.id,
          amount: parsed,
        };
      })
      .filter((change): change is NonNullable<typeof change> => Boolean(change));

    if (changes.length === 0) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await updateFuelAmounts.mutateAsync({ tripId, updates: changes });
      setIsEditing(false);
    } catch (mutationError) {
      const message = mutationError instanceof Error ? mutationError.message : t('errorGeneric');
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (fuelEntries.length === 0) {
    return null;
  }

  return (
    <section className="rounded-3xl border border-[#f1f1f1] bg-white px-4 py-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] md:px-8 md:py-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-[#f9f9f9] p-2">
              <img src="/assets/icons/gas-station.svg" alt="" className="h-6 w-6" />
            </div>
            <div className="flex min-w-0 items-baseline gap-2">
              <p className="text-[1.5rem] leading-9 font-medium text-[#292929]">
                {t('totalLabel', { amount: formattedTotalAmount })}
              </p>
              <span className="text-sm leading-5 text-[#292929]">{t('totalUnit')}</span>
            </div>
          </div>
          {canEdit ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {isEditing ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="flex h-10 w-full items-center justify-center rounded-full px-4 py-2 text-sm font-medium text-[#292929] transition-colors hover:bg-[#f9f9f9] sm:w-auto"
                  >
                    {formActionsT('cancel')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving || hasValidationError || !hasChanges}
                    className="flex h-10 w-full items-center justify-center rounded-full bg-[#171716] px-4 py-2 text-sm font-medium text-white transition-transform hover:scale-[0.99] disabled:cursor-not-allowed disabled:bg-[#171716] disabled:opacity-50 sm:w-auto"
                  >
                    {isSaving ? (
                      <Spinner variant="circle" size={16} strokeWidth={2} className="text-white" />
                    ) : null}
                    {formActionsT('save')}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={handleToggleEdit}
                  className="flex h-10 w-full items-center justify-center rounded-full bg-[#171716] px-4 py-2 text-sm font-medium text-white transition-transform hover:scale-[0.99] sm:w-auto"
                >
                  {t('editButton')}
                </Button>
              )}
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="rounded-2xl bg-[#ffe8e8] px-4 py-3 text-sm text-[#ba1a1a]">{error}</div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {fuelEntries.map((entry) => {
            const draftValue = values[entry.fuel.id] ?? '';
            const isInvalid = Number.isNaN(Number(draftValue)) || draftValue.trim().length === 0;

            return (
              <div
                key={`${entry.cityId}-${entry.fuel.id}`}
                className="flex flex-col gap-4 rounded-[1.5rem] border border-[#f1f1f1] bg-[#f9f9f9] p-5"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2">
                    <img src="/assets/icons/gas-station.svg" alt="" className="h-5 w-5" />
                  </div>
                  <span className="min-w-0 text-lg leading-5 text-[#292929]">
                    {entry.fuel.carModel || t('carModelFallback')}
                  </span>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col">
                    <h3 className="text-[1.25rem] leading-7 font-medium text-[#292929]">
                      {entry.cityName}
                    </h3>
                    <p className="text-sm leading-5 text-[#666666]">
                      {entry.fuel.fuelTypeName || t('fuelTypeFallback')}
                    </p>
                  </div>
                  {isEditing ? (
                    <Input
                      value={draftValue}
                      onChange={(event) => handleAmountChange(entry.fuel.id, event.target.value)}
                      className={cn(
                        'h-10 w-full max-w-[6rem] rounded-[0.75rem] border border-[#171716] bg-white text-center text-xl font-medium text-[#292929] md:text-xl',
                        'focus-visible:border-[#171716] focus-visible:ring-0',
                        isInvalid ? 'border-[#e5484d]' : 'border-[#171716]'
                      )}
                      inputMode="decimal"
                    />
                  ) : (
                    <div className="flex h-10 w-full max-w-[6rem] items-center justify-center rounded-[0.75rem] border border-[#e4e4e4] bg-white text-xl font-medium text-[#292929] md:text-xl">
                      {numberFormatter.format(entry.fuel.fuelAmount ?? 0)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default TripFuelManagementCard;
